<template>
  <div class="payment-links-section">
    <!-- Loading state -->
    <div v-if="isLoading" class="section-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span>{{ t('Carregando links...') }}</span>
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Active links list -->
      <div v-if="activeLinks.length > 0" class="active-links-list">
        <div class="active-links-header">
          <span class="section-title">{{ t('Links de pagamento ativos') }} ({{ activeLinks.length }})</span>
          <Button
            data-testid="payment-links-section-create-new-button"
            :label="t('Criar novo')"
            icon="pi pi-plus"
            severity="primary"
            class="p-button-sm"
            @click="handleCreateLink"
          />
        </div>

        <div
          v-for="link in activeLinks"
          :key="link.id"
          class="active-link-summary"
        >
          <div class="summary-header">
            <span class="summary-title">{{ formatCurrency((link.amount ?? 0) / 100) }}</span>
            <PaymentLinksStatusBadge :status="link.status" />
          </div>

          <div class="summary-content">
            <div class="summary-details">
              <span v-if="(link.installments ?? 0) > 1">
                {{ link.installments }}x de {{ formatCurrency((link.amount ?? 0) / (link.installments ?? 1) / 100) }}
              </span>
              <span v-if="link.due_date">
                {{ t('Vencimento') }}: {{ formatDate(link.due_date) }}
              </span>
              <span v-if="link.description" class="link-description">
                {{ link.description }}
              </span>
            </div>
          </div>

          <div class="summary-actions">
            <Button
              data-testid="payment-links-section-copy-link-button"
              :label="t('Copiar link')"
              icon="pi pi-copy"
              class="p-button-outlined p-button-sm"
              @click="handleCopyUrlForLink(link)"
            />
            <Button
              data-testid="payment-links-section-whatsapp-button"
              :label="t('WhatsApp')"
              icon="pi pi-whatsapp"
              severity="success"
              class="p-button-sm"
              @click="handleSendWhatsAppForLink(link)"
            />
            <Button
              data-testid="payment-links-section-view-details-button"
              icon="pi pi-ellipsis-v"
              class="p-button-text p-button-sm"
              v-tooltip.top="t('Ver detalhes')"
              @click="handleViewDetailsForLink(link)"
            />
          </div>
        </div>
      </div>

      <!-- No links at all - show create button -->
      <div v-else-if="!hasLinks" class="no-links">
        <p>{{ t('Nenhum link de pagamento criado') }}</p>
        <Button
          data-testid="payment-links-section-create-link-button"
          :label="t('Criar link de pagamento')"
          icon="pi pi-plus"
          severity="primary"
          @click="handleCreateLink"
        />
      </div>

      <!-- Has links but none active -->
      <div v-else class="links-inactive">
        <p>{{ t('Nenhum link ativo') }}</p>
        <Button
          data-testid="payment-links-section-create-new-link-button"
          :label="t('Criar novo link')"
          icon="pi pi-plus"
          severity="primary"
          class="p-button-sm"
          @click="handleCreateLink"
        />
        <Button
          data-testid="payment-links-section-view-all-button"
          :label="t('Ver todos')"
          icon="pi pi-list"
          class="p-button-text p-button-sm"
          @click="handleViewAll"
        />
      </div>

      <!-- Links history (collapsible) - shows non-active links -->
      <Accordion v-if="linksHistory.length > 0" class="links-accordion">
        <AccordionTab :header="`${t('Histórico de links')} (${linksHistory.length})`">
          <div class="links-list">
            <PaymentLinksCard
              v-for="link in linksHistory"
              :key="link.id"
              :payment-link="link"
              :show-actions="true"
              @send-whatsapp="handleSendWhatsAppForLink"
            />
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { usePaymentLinks, STATUS } from '../../composables/usePaymentLinks';
import { useDateFormatter } from '../../composables/useDateFormatter';
import PaymentLinksStatusBadge from './PaymentLinksStatusBadge.vue';
import PaymentLinksCard from './PaymentLinksCard.vue';

interface PaymentLink {
  id?: number | string;
  status?: string;
  amount?: number;
  installments?: number;
  due_date?: string | null;
  description?: string;
  url?: string;
  payment_method?: string;
  [key: string]: unknown;
}

interface LinkCreatedPayload {
  owner: string;
  ownerId: number | string;
  paymentLink?: PaymentLink;
}

interface LinkCancelledPayload {
  paymentLinkId?: number | string;
}

const props = withDefaults(
  defineProps<{
    owner: string;
    ownerId: number | string;
    defaultAmount?: number;
    customerPhone?: string;
    customerName?: string;
  }>(),
  {
    defaultAmount: 0,
    customerPhone: '',
    customerName: '',
  }
);

const emit = defineEmits<{
  (e: 'link-created', data: LinkCreatedPayload): void;
  (e: 'link-cancelled', data: LinkCancelledPayload): void;
  (e: 'refresh-requested'): void;
}>();

const { t } = useI18n();
const { listByOwner } = usePaymentLinks();
const { formatDate: formatDateLocale } = useDateFormatter();

// State
const isLoading = ref<boolean>(false);
const links = ref<PaymentLink[]>([]);

// Computed
const hasLinks = computed<boolean>(() => links.value.length > 0);

const activeLinks = computed<PaymentLink[]>(() => {
  return links.value.filter(link => link.status === STATUS.ACTIVE);
});

const linksHistory = computed<PaymentLink[]>(() => {
  // Return all non-active links for history
  return links.value.filter(link => link.status !== STATUS.ACTIVE);
});

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const loadLinks = async (): Promise<void> => {
  if (!props.owner || !props.ownerId) return;

  log('PaymentLinksSection', 'loadLinks', 'Loading links', { owner: props.owner, ownerId: props.ownerId });
  isLoading.value = true;

  try {
    links.value = await listByOwner(props.owner, props.ownerId) as unknown as PaymentLink[];
    log('PaymentLinksSection', 'loadLinks', 'Loaded links', links.value.length);
  } catch (error) {
    log('PaymentLinksSection', 'loadLinks', 'Error', error);
  } finally {
    isLoading.value = false;
  }
};

const handleCreateLink = (): void => {
  log('PaymentLinksSection', 'handleCreateLink', 'Opening create modal');
  eventBus.emit('modal.payment-link.open', {
    owner: props.owner,
    ownerId: props.ownerId,
    defaultAmount: props.defaultAmount,
    customerPhone: props.customerPhone,
    customerName: props.customerName
  });
};

const handleViewDetailsForLink = (link: PaymentLink): void => {
  if (!link) return;
  log('PaymentLinksSection', 'handleViewDetailsForLink', 'Opening details modal', link.id);
  eventBus.emit('modal.payment-link.open', {
    id: link.id,
    customerPhone: props.customerPhone,
    customerName: props.customerName
  });
};

const handleViewAll = (): void => {
  // Open modal with first link or create new
  if (links.value.length > 0) {
    eventBus.emit('modal.payment-link.open', {
      id: links.value[0].id,
      customerPhone: props.customerPhone,
      customerName: props.customerName
    });
  } else {
    handleCreateLink();
  }
};

const handleCopyUrlForLink = async (link: PaymentLink): Promise<void> => {
  if (!link?.url) return;

  try {
    await navigator.clipboard.writeText(link.url);
    eventBus.emit('globalMessage.success', t('Link copiado!'));
  } catch (error) {
    log('PaymentLinksSection', 'handleCopyUrlForLink', 'Error', error);
  }
};

const handleSendWhatsAppForLink = (link: PaymentLink): void => {
  sendWhatsAppMessage(link);
};

const sendWhatsAppMessage = (link: PaymentLink): void => {
  const phone = props.customerPhone?.replace(/\D/g, '') || '';
  const name = props.customerName || t('cliente');
  const amount = formatCurrency((link.amount ?? 0) / 100);

  // Build message directly without i18n (placeholders don't work well with i18n)
  const message = `Olá ${name}! Segue o link para pagamento de ${amount}: ${link.url}`;

  const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
};

// Event handlers
const handleLinkCreated = (data?: LinkCreatedPayload): void => {
  if (!data) return;
  if (data.owner === props.owner && String(data.ownerId) === String(props.ownerId)) {
    log('PaymentLinksSection', 'handleLinkCreated', 'Link created, reloading');
    loadLinks();
    emit('link-created', data);
  }
};

const handleLinkCancelled = (data?: LinkCancelledPayload): void => {
  log('PaymentLinksSection', 'handleLinkCancelled', 'Link cancelled, reloading');
  loadLinks();
  emit('link-cancelled', data ?? {});
};

// Watch for prop changes
watch([() => props.owner, () => props.ownerId], () => {
  loadLinks();
});

// Lifecycle
onMounted(() => {
  log('PaymentLinksSection', 'onMounted', 'Component mounted');

  loadLinks();

  // Listen for payment link events
  eventBus.on('payment-link.created', handleLinkCreated);
  eventBus.on('payment-link.cancelled', handleLinkCancelled);
});

onBeforeUnmount(() => {
  eventBus.off('payment-link.created', handleLinkCreated);
  eventBus.off('payment-link.cancelled', handleLinkCancelled);
});

// Expose methods
defineExpose({
  refresh: loadLinks
});
</script>

<style scoped>
.payment-links-section {
  padding: 0;
}

.section-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: var(--text-color-secondary);
}

.active-links-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.active-links-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.section-title {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.9rem;
}

.active-link-summary {
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  border-left: 3px solid var(--blue-500);
}

.link-description {
  display: block;
  margin-top: 0.25rem;
  font-style: italic;
  color: var(--text-color-secondary);
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.summary-title {
  font-weight: 600;
  color: var(--text-color);
}

.summary-content {
  margin-bottom: 0.75rem;
}

.summary-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.summary-details {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  flex-wrap: wrap;
}

.summary-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.no-links,
.links-inactive {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  text-align: center;
}

.no-links p,
.links-inactive p {
  margin: 0;
  color: var(--text-color-secondary);
}

.links-inactive {
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
}

.links-accordion {
  margin-top: 1rem;
}

.links-accordion :deep(.p-accordion-header-link) {
  background-color: transparent;
  border: 1px solid var(--surface-border);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
}

.links-accordion :deep(.p-accordion-content) {
  padding: 1rem 0;
  border: none;
}

.links-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .summary-actions {
    flex-direction: column;
  }

  .summary-actions button {
    width: 100%;
  }

  .links-inactive {
    flex-direction: column;
  }
}
</style>
